/**
 * Run-scoped signal that browser sessions should show their replay even
 * though the live screencast is still connected.
 *
 * When a run pauses for exception guidance (`awaiting_guidance`), the browser
 * pod is kept alive for resume, so the live-view SSE never disconnects and the
 * usual live→replay swap (driven by the screencast ending) never fires. The
 * run page knows the run is paused; this context carries that one bit down to
 * every browser surface (the Browser-Operator pill/expanded overlay, the
 * inline run-log card, and the Rain attachment preview) without threading a
 * prop through the output-type-agnostic renderers in between.
 *
 * Outside a provider (e.g. browser output rendered off a run page) the default
 * is `false`, preserving the screencast-driven behavior.
 *
 * @module browser-automation/context/browser-replay-context
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';

export interface IBrowserReplayContextValue {
  /**
   * Treat browser sessions as ended-for-replay even while the live screencast
   * is connected. Set when the run is in a non-live pause (currently
   * `awaiting_guidance`) that keeps the browser pod alive.
   */
  forceSessionEnded: boolean;
}

const BrowserReplayContext = createContext<IBrowserReplayContextValue>({
  forceSessionEnded: false,
});

export interface IBrowserReplayProviderProps {
  forceSessionEnded: boolean;
  children: ReactNode;
}

export function BrowserReplayProvider({
  forceSessionEnded,
  children,
}: IBrowserReplayProviderProps) {
  const value = useMemo(() => ({ forceSessionEnded }), [forceSessionEnded]);
  return (
    // eslint-disable-next-line react-x/no-context-provider -- reason: Context.Provider is correct for React 18 (project is not on React 19)
    <BrowserReplayContext.Provider value={value}>
      {children}
    </BrowserReplayContext.Provider>
  );
}

export function useBrowserReplay(): IBrowserReplayContextValue {
  return useContext(BrowserReplayContext);
}
